import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormInputField,
  FormTextarea,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { zodResolver } from "@hookform/resolvers/zod";
import type { IIdpGroup } from "@models";
import { useCreateIdpGroup, useUpdateIdpGroup } from "@query";
import { APP_BASE_PATH } from "@shared";
import { useForm } from "react-hook-form";
import { z } from "zod";

const createIdpGroupFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, t("hub.administer.add_user_required"))
      .max(255, t("hub.administer.idp_group_name_max")),
    description: z.string().optional(),
  });

type IdpGroupFormData = z.infer<ReturnType<typeof createIdpGroupFormSchema>>;

interface IAddIdpGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyUuid: string | undefined;
  group?: IIdpGroup | null;
}

export function AddIdpGroupDialog({
  open,
  onOpenChange,
  companyUuid,
  group,
}: IAddIdpGroupDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = Boolean(group?.id);

  const form = useForm<IdpGroupFormData>({
    resolver: zodResolver(createIdpGroupFormSchema(t)),
    mode: "onChange",
    defaultValues: { name: "", description: "" },
    values: open
      ? {
          name: group?.name ?? "",
          description: group?.description ?? "",
        }
      : undefined,
  });

  const { control, handleSubmit, formState } = form;

  const { mutate: createGroup, isPending: isCreating } = useCreateIdpGroup(
    companyUuid,
    APP_BASE_PATH,
    {
      onSuccess: (created) => {
        toast.add({
          title: t("hub.administer.idp_group_add_success"),
          description: t("hub.administer.idp_group_add_success_description", {
            defaultValue: `"${created.name}" was added successfully.`,
          }),
          data: { type: "success" },
        });
        onOpenChange(false);
      },
      onError: () => {
        toast.add({
          title: t("hub.administer.idp_group_add_error"),
          description: t("hub.administer.idp_group_add_error_description"),
          data: { type: "error" },
        });
      },
    },
  );

  const { mutate: updateGroup, isPending: isUpdating } = useUpdateIdpGroup(
    companyUuid,
    APP_BASE_PATH,
    {
      onSuccess: (updated) => {
        toast.add({
          title: t("hub.administer.idp_group_update_success"),
          description: t(
            "hub.administer.idp_group_update_success_description",
            {
              defaultValue: `"${updated.name}" was updated successfully.`,
            },
          ),
          data: { type: "success" },
        });
        onOpenChange(false);
      },
      onError: () => {
        toast.add({
          title: t("hub.administer.idp_group_update_error"),
          description: t("hub.administer.idp_group_update_error_description"),
          data: { type: "error" },
        });
      },
    },
  );

  const isSubmitting = isCreating || isUpdating;

  const onSubmit = (data: IdpGroupFormData) => {
    const name = data.name.trim();
    const description = data.description?.trim() || undefined;
    if (isEdit && group) {
      updateGroup({
        groupId: group.id,
        body: {
          name,
          description,
          isActive: group.isActive !== false,
        },
      });
      return;
    }
    createGroup({ name, description, isActive: true });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("hub.administer.edit_idp_group_dialog_title")
              : t("hub.administer.add_idp_group_dialog_title")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form {...form}>
            <form
              id="idp-group-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4">
              <FormInputField<IdpGroupFormData>
                control={control}
                name="name"
                label={t("hub.administer.idp_group_name")}
                placeholder={t("hub.administer.idp_group_name_placeholder")}
                required
              />
              <FormTextarea<IdpGroupFormData>
                control={control}
                name="description"
                label={t("hub.administer.idp_group_description")}
                placeholder={t(
                  "hub.administer.idp_group_description_placeholder",
                )}
              />
            </form>
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("hub.administer.cancel")}
          </Button>
          <Button
            type="submit"
            form="idp-group-form"
            disabled={!formState.isValid || isSubmitting}>
            {isSubmitting
              ? isEdit
                ? t("hub.administer.edit_user_submitting")
                : t("hub.administer.idp_group_submitting")
              : isEdit
                ? t("hub.administer.save")
                : t("hub.administer.add_idp_group_submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
